import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Search, Plus, Edit2, Trash2, Eye, X, Check, CheckCircle, XCircle,
  MessageSquare, Star, Image as ImageIcon, User
} from 'lucide-react';
import * as LucideIcons from "lucide-react";

const TestimonialAdmin = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  // Form State
  const [isEditing, setIsEditing] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    rating: 5,
    review: "",
    image: null,
    status: true
  });

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4100";

  // ==================== FETCH DATA ====================
  const fetchReviews = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/admin/managetestimonials/gettestimonial`, {
        withCredentials: true
      });
      // Handle different response structures if needed
      const data = res.data.testimonials || res.data;
      if (Array.isArray(data)) {
        setReviews(data);
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // ==================== HANDLERS ====================
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const openAddModal = () => {
    setIsEditing(false);
    setFormData({ name: "", rating: 5, review: "", image: null, status: true });
    setImagePreview(null);
    setShowModal(true);
  };

  const openEditModal = (review) => {
    setIsEditing(true);
    setFormData({
      name: review.name,
      rating: review.rating,
      review: review.review,
      status: review.status,
      image: null // Reset image file input
    });
    // Set preview to existing image URL
    if (review.image) {
      setImagePreview(`${API_URL}/uploads/${review.image}`);
    } else {
      setImagePreview(null);
    }
    setSelectedReview(review);
    setShowModal(true);
  };

  const handleView = (review) => {
    setSelectedReview(review);
    setViewModal(true);
  };

  // ==================== API ACTIONS ====================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.review) return alert("Name and Review are required");

    const data = new FormData();
    data.append("name", formData.name);
    data.append("rating", formData.rating);
    data.append("review", formData.review);
    data.append("status", formData.status);
    if (formData.image) {
      data.append("image", formData.image);
    }

    try {
      let res;
      if (isEditing) {
        res = await axios.put(`${API_URL}/admin/managetestimonials/updatetestimonial/${selectedReview._id}`, data, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        });
      } else {
        res = await axios.post(`${API_URL}/admin/managetestimonials/addtestimonial`, data, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      if (res.status === 200 || res.status === 201) {
        fetchReviews();
        setShowModal(false);
        alert(isEditing ? "Review Updated!" : "Review Added!");
      }
    } catch (error) {
      console.error("Error saving review:", error);
      alert(error.response?.data?.message || "Failed to save review");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;

    try {
      const res = await axios.delete(`${API_URL}/admin/managetestimonials/deletetestimonial/${id}`, { withCredentials: true });
      if (res.status === 200) {
        fetchReviews();
      }
    } catch (error) {
      console.error("Error deleting review:", error);
      alert("Failed to delete");
    }
  };

  const handleToggleStatus = async (review) => {
    try {
      // Optimistic Update
      const updatedReviews = reviews.map(r =>
        r._id === review._id ? { ...r, status: !r.status } : r
      );
      setReviews(updatedReviews);

      await axios.put(`${API_URL}/admin/managetestimonials/toggletestimonial/${review._id}`, {}, { withCredentials: true });

    } catch (error) {
      console.error("Error toggling status:", error);
      fetchReviews(); // Revert on error
      alert("Failed to update status");
    }
  };

  // ==================== FILTERING ====================
  const filteredReviews = reviews.filter(r =>
    r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.review.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 p-8 font-sans text-slate-900">

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">Client Testimonials</h1>
          <p className="text-slate-500 mt-1">Manage what your clients say about you</p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition-all transform hover:scale-105"
        >
          <Plus size={20} /> Add New Review
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 mb-6 flex items-center gap-3">
        <Search className="text-slate-400" size={20} />
        <input
          type="text"
          placeholder="Search reviews..."
          className="flex-1 outline-none text-slate-700 placeholder-slate-400"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Reviews Table */}
      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading reviews...</div>
        ) : filteredReviews.length === 0 ? (
          <div className="p-12 text-center text-slate-400">No reviews found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-bold">
                  <th className="p-6">Client</th>
                  <th className="p-6">Review</th>
                  <th className="p-6">Rating</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredReviews.map(review => (
                  <tr key={review._id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 border border-slate-200">
                          {review.image ? (
                            <img src={`${API_URL}/uploads/${review.image}`} alt={review.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400"><User size={20} /></div>
                          )}
                        </div>
                        <h3 className="font-bold text-slate-800 text-base">{review.name}</h3>
                      </div>
                    </td>
                    <td className="p-6">
                      <p className="text-slate-500 text-sm line-clamp-2 max-w-xs italic">"{review.review}"</p>
                    </td>
                    <td className="p-6">
                      <div className="flex text-yellow-500 gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={14} fill={i < review.rating ? "currentColor" : "none"} className={i < review.rating ? "" : "text-slate-300"} />
                        ))}
                      </div>
                    </td>
                    <td className="p-6">
                      <button
                        onClick={() => handleToggleStatus(review)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-colors ${review.status
                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                            : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                      >
                        {review.status ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        {review.status ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="p-6 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleView(review)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="View">
                          <Eye size={18} />
                        </button>
                        <button onClick={() => openEditModal(review)} className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Edit">
                          <Edit2 size={18} />
                        </button>
                        <button onClick={() => handleDelete(review._id)} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Delete">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-xl text-slate-800">{isEditing ? "Edit Review" : "Add New Review"}</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">

              {/* Name */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Client Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-slate-700"
                  placeholder="e.g. John Doe"
                />
              </div>

              {/* Rating */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRatingChange(star)}
                      className={`p-2 transition-transform hover:scale-110 ${formData.rating >= star ? "text-yellow-400" : "text-slate-200"}`}
                    >
                      <Star size={32} fill={formData.rating >= star ? "currentColor" : "none"} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Review */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Review</label>
                <textarea
                  name="review"
                  value={formData.review}
                  onChange={handleInputChange}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-slate-700 min-h-[120px]"
                  placeholder="Write the client's feedback here..."
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Client Photo (Optional)</label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400"><ImageIcon size={24} /></div>
                    )}
                  </div>
                  <input
                    type="file"
                    onChange={handleImageChange}
                    accept="image/*"
                    className="block w-full text-sm text-slate-500
                       file:mr-4 file:py-2 file:px-4
                       file:rounded-full file:border-0
                       file:text-sm file:font-semibold
                       file:bg-indigo-50 file:text-indigo-700
                       hover:file:bg-indigo-100
                     "
                  />
                </div>
              </div>

              {/* Status */}
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className={`w-12 h-7 rounded-full p-1 transition-colors duration-300 ${formData.status ? "bg-emerald-500" : "bg-slate-200"}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${formData.status ? "translate-x-5" : ""}`}></div>
                </div>
                <input
                  type="checkbox"
                  name="status"
                  checked={formData.status}
                  onChange={handleInputChange}
                  className="hidden"
                />
                <span className="font-semibold text-slate-700 group-hover:text-indigo-600 transition-colors">
                  {formData.status ? "Active (Visible)" : "Inactive (Hidden)"}
                </span>
              </label>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 transition-all transform hover:scale-[1.02]"
                >
                  {isEditing ? "Save Changes" : "Add Review"}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewModal && selectedReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl relative">
            <button onClick={() => setViewModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
              <X size={24} />
            </button>
            <div className="w-24 h-24 rounded-full bg-slate-100 border-4 border-white shadow-lg overflow-hidden mx-auto mb-6">
              {selectedReview.image ? (
                <img src={`${API_URL}/uploads/${selectedReview.image}`} alt={selectedReview.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400"><User size={40} /></div>
              )}
            </div>
            <h3 className="text-2xl font-black text-slate-800 mb-1">{selectedReview.name}</h3>

            <div className="flex justify-center text-yellow-500 gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20} fill={i < selectedReview.rating ? "currentColor" : "none"} className={i < selectedReview.rating ? "" : "text-slate-200"} />
              ))}
            </div>

            <div className="relative bg-slate-50 p-6 rounded-2xl mb-6">
              <MessageSquare className="absolute top-0 left-0 -mt-2 -ml-2 text-indigo-200 transform -scale-x-100" size={32} />
              <p className="text-slate-600 italic leading-relaxed">"{selectedReview.review}"</p>
            </div>

            <div className="px-4 py-2 bg-slate-100 rounded-lg inline-block text-sm font-bold text-slate-600">
              {selectedReview.status ? "Active Review" : "Hidden Review"}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TestimonialAdmin;